import { useQuery } from '@tanstack/react-query';
import { API_URL } from '../config';
import { toApiError } from '../lib/api-error';

export interface CanonicalPage {
  id: string;
  url: string;
  title?: string | null;
  latestDescription?: string | null;
  status?: string;
}

export type CanonicalPageSummary = Pick<CanonicalPage, 'id' | 'url' | 'title'>;

interface UseCanonicalPagesOptions {
  enabled?: boolean;
  pageSize?: number;
}

interface UseCanonicalPageSummaryOptions {
  enabled?: boolean;
}

export function buildCanonicalPageSummaryUrl(params: {
  apiUrl: string;
  propertyId: string;
  ids: string[];
}): string {
  const search = new URLSearchParams();
  search.set('ids', params.ids.join(','));
  return `${params.apiUrl}/properties/${params.propertyId}/canonical-pages/summary?${search}`;
}

export function useCanonicalPages(propertyId: string | null | undefined, options?: UseCanonicalPagesOptions) {
  const enabled = options?.enabled ?? !!propertyId;
  const pageSize = options?.pageSize ?? 200;

  return useQuery({
    queryKey: ['canonical-pages', propertyId, pageSize],
    enabled,
    queryFn: async () => {
      if (!propertyId) {
        return [];
      }
      const params = new URLSearchParams({ pageSize: String(pageSize) });
      const response = await fetch(`${API_URL}/properties/${propertyId}/canonical-pages?${params}`, {
        credentials: 'include',
      });
      if (!response.ok) {
        throw await toApiError(response, 'Failed to fetch canonical pages');
      }
      const data = await response.json();
      return (data.pages ?? []) as CanonicalPage[];
    },
  });
}

export function useCanonicalPageSummary(
  propertyId: string | null | undefined,
  canonicalPageId: string | null | undefined,
  options?: UseCanonicalPageSummaryOptions
) {
  const enabled = options?.enabled ?? Boolean(propertyId && canonicalPageId);

  return useQuery({
    queryKey: ['canonical-page-summary', propertyId, canonicalPageId],
    enabled,
    staleTime: 60_000,
    queryFn: async () => {
      if (!propertyId || !canonicalPageId) {
        return null;
      }

      const response = await fetch(
        buildCanonicalPageSummaryUrl({
          apiUrl: API_URL,
          propertyId,
          ids: [canonicalPageId],
        }),
        { credentials: 'include' }
      );
      if (!response.ok) {
        throw await toApiError(response, 'Failed to fetch canonical page summary');
      }
      const data = await response.json();
      const pages = (data.pages ?? []) as CanonicalPageSummary[];
      return pages.find((page) => page.id === canonicalPageId) ?? null;
    },
  });
}
